/**
 * tokens.ts — плоский набор дизайн-токенов проекта Parallax.
 *
 * Все цвета — CSS-переменные, которые MUI генерирует в режиме
 * `cssVariables: { colorSchemeSelector: 'data' }`. При переключении темы
 * (атрибут data-mui-color-scheme="light|dark" на <html>) браузер
 * автоматически пересчитывает значения — React в этом не участвует.
 *
 * Использование в styled-компонентах:
 *
 *   import { t } from "../tokens";
 *
 *   const StyledTab = styled(Tab)({
 *       color: t.text.secondary,
 *       "&&.Mui-selected": { color: t.text.primary },
 *       "&.Mui-focusVisible": {
 *           outline: `2px solid ${t.primary.alpha(0.6)}`,
 *       },
 *   });
 *
 * Преимущества по сравнению с `theme.palette.*` / `theme.vars.palette.*`:
 *   1. В стилях нет упоминаний `theme` — стили независимы от runtime React.
 *   2. emotion может статически закэшировать строку — без перекомпиляции.
 *   3. TypeScript подсказывает токены, опечатки ловятся компилятором.
 *   4. Тема переключается без перерендеров, через смену атрибута <html>.
 *
 * Когда токен НЕДОСТАТОЧЕН и нужен `theme` напрямую:
 *   - theme.transitions.create(...)
 *   - theme.breakpoints.down('sm')
 *   - theme.shape.borderRadius
 *   Эти три API не имеют CSS-переменных и читаются через объект темы как обычно.
 */

// =============================================================================
// Helper: создание токена с alpha через MUI-овский *Channel
// =============================================================================
/**
 * MUI генерирует две переменные на каждый цвет:
 *   --mui-palette-primary-main          → "hsl(225, 75%, 62%)"
 *   --mui-palette-primary-mainChannel   → "225 75% 62%" (без alpha)
 *
 * Channel-переменная нужна, чтобы делать `rgb(var(...) / 0.5)` —
 * это единственный корректный способ применить alpha к цвету из темы,
 * когда сам цвет приходит как CSS-переменная.
 */
const channel = (varName: string) => (a: number) =>
    `rgb(var(--mui-palette-${varName}) / ${a})`;

const v = (varName: string) => `var(--mui-palette-${varName})`;

// =============================================================================
// Color tokens
// =============================================================================

/** Основной акцент (синие CTA, фокус-кольца, активные элементы) */
export const primary = {
    main: v("primary-main"),
    light: v("primary-light"),
    dark: v("primary-dark"),
    contrastText: v("primary-contrastText"),
    /** Прозрачность от primary.main. Пример: `primary.alpha(0.12)` */
    alpha: channel("primary-mainChannel"),
} as const;

/** Семантический success (зелёный) */
export const success = {
    main: v("success-main"),
    light: v("success-light"),
    dark: v("success-dark"),
    contrastText: v("success-contrastText"),
    alpha: channel("success-mainChannel"),
} as const;

/** Семантический warning (янтарный) */
export const warning = {
    main: v("warning-main"),
    light: v("warning-light"),
    dark: v("warning-dark"),
    contrastText: v("warning-contrastText"),
    alpha: channel("warning-mainChannel"),
} as const;

/** Семантический error (crimson) */
export const error = {
    main: v("error-main"),
    light: v("error-light"),
    dark: v("error-dark"),
    contrastText: v("error-contrastText"),
    alpha: channel("error-mainChannel"),
} as const;

/** Семантический info (используется редко, как нейтральная сигнальная окраска) */
export const info = {
    main: v("info-main"),
    light: v("info-light"),
    dark: v("info-dark"),
    contrastText: v("info-contrastText"),
    alpha: channel("info-mainChannel"),
} as const;

/** Текст. Используется в `color: ...` */
export const text = {
    /** Заголовки, основной текст */
    primary: v("text-primary"),
    /** Подписи, неактивные метки, плейсхолдеры */
    secondary: v("text-secondary"),
    /** Отключённое состояние */
    disabled: v("text-disabled"),
    alpha: channel("text-primaryChannel"),
    secondaryAlpha: channel("text-secondaryChannel"),
} as const;

/** Фон. Используется в `backgroundColor: ...` */
export const bg = {
    /** Фон страницы */
    default: v("background-default"),
    /** Фон карточек, диалогов, popover */
    paper: v("background-paper"),
    /**
     * Прозрачность от background.paper — для полупрозрачных (glass) панелей
     * поверх карты. Пример: `bg.paperAlpha(0.85)` + `backdrop-filter: blur(8px)`.
     */
    paperAlpha: channel("background-paperChannel"),
} as const;

/**
 * Action — для hover / selected / disabled подсветок.
 * В отличие от MUI-овских action.hover (alpha от base color),
 * эти токены уже учитывают тему и в обеих темах дают видимую подсветку.
 */
export const action = {
    hover: v("action-hover"),
    selected: v("action-selected"),
    disabled: v("action-disabled"),
    disabledBackground: v("action-disabledBackground"),
    focus: v("action-focus"),
    active: v("action-active"),
} as const;

/** Линии-разделители */
export const divider = v("divider");

/**
 * Разделитель с прозрачностью — для внутренних линий таблиц: полный
 * divider между строками читается как рамка и рвёт список на блоки.
 */
export const dividerAlpha = channel("dividerChannel");

/** Common — общие цвета (белый/чёрный) */
export const common = {
    white: v("common-white"),
    black: v("common-black"),
} as const;

// =============================================================================
// Non-color tokens (radii, shadows, spacing scale)
// =============================================================================

/**
 * Радиусы. Не CSS-переменные (MUI их не генерирует),
 * но фиксируем как константы из дизайн-системы.
 */
export const radius = {
    /**
     * Мелкие плашки высотой ~16 px: скелетоны строк текста, микро-бейджи.
     * Больше нельзя: при радиусе 8 у 16-пиксельной полоски скругление
     * равно половине высоты, и она читается как капсула, а не как
     * строка на месте будущего текста.
     */
    xs: 4,
    /** Маленькие иконочные кнопки, чипы */
    sm: 6,
    /** Input, кнопка, бейдж, тег (стандарт) */
    md: 8,
    /** Карточка, диалог, popover */
    lg: 12,
    /** Аватар, чип-токен (pill) */
    full: 999,
} as const;

/**
 * Spacing-шкала из дизайн-системы.
 * Используется как `spacing.md` → `16px`.
 * Параллельно работает с MUI's `sx={{ p: 2 }}` — это эквивалент `spacing.md`.
 */
export const spacing = {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
} as const;

/**
 * Тени из темы (`--mui-shadows-1` .. `--mui-shadows-24`).
 * Для popover / dropdown / dialog — используем shadows.popover, shadows.dialog.
 */
export const shadows = {
    /** Без тени */
    none: "none",
    /** Лёгкая поднятость (карточка при hover, dropdown) */
    sm: "var(--mui-shadows-2)",
    /** Popover, menu */
    md: "var(--mui-shadows-8)",
    /** Dialog */
    lg: "var(--mui-shadows-24)",
    /** Кастомный фокус-ring (3px primary @ alpha) */
    focusRing: `0 0 0 3px ${primary.alpha(0.25)}`,
    /**
     * Панель, всплывающая НАД контентом снизу (sticky-бар действий).
     * Своя, а не из MUI: все тени темы направлены ВНИЗ, а этой нужно
     * показать, что панель лежит поверх того, что над ней.
     * Чёрный здесь литералом намеренно — тень чёрная в обеих темах,
     * это не палитровый цвет (так же устроены и сами MUI-тени).
     */
    overlayTop: "0 -6px 16px rgb(0 0 0 / 0.32)",
} as const;

/**
 * Длительности анимаций из дизайн-системы.
 * (theme.transitions.create(...) тоже работает — но эти константы
 * полезны для прямого CSS `transition: ... 150ms`.)
 */
export const duration = {
    /** Быстрое: hover, focus, color shift */
    fast: 120,
    /** Стандарт: большинство transition */
    base: 150,
    /** Заметные переходы: indicator табов, accordion expand */
    slow: 200,
} as const;

/** Стандартный easing для функциональных переходов */
export const easing = "cubic-bezier(0.2, 0, 0, 1)";

/**
 * Семейства шрифтов. Основное задаётся темой (typography.fontFamily) — здесь
 * только моноширинное: оно применяется точечно, к числам и идентификаторам,
 * и потому нужно компонентам напрямую.
 *
 * Courier Prime — единственный моношрифт, реально поставляемый приложением:
 * подключается через `@fontsource/courier-prime` в src/index.tsx. Остальное
 * в стеке — системные фолбэки на случай, если шрифт не загрузился.
 *
 * Тот же стек продублирован в классе `.mono` в src/styles/global.css. Если
 * когда-нибудь появится другой моношрифт — менять нужно В ОБОИХ местах
 * одновременно и не забыть добавить его импорт в index.tsx: имя, которого
 * нет в зависимостях, молча не резолвится, и вместо ожидаемого шрифта
 * рисуется следующий из стека.
 */
export const font = {
    mono: `'Courier Prime', ui-monospace, SFMono-Regular, Menlo, monospace`,
} as const;

// =============================================================================
// Default export — для импорта всех токенов разом как `t.X.Y`
// =============================================================================

/**
 * Удобный shortcut. Используется как:
 *   import { t } from "../tokens";
 *   color: t.text.primary;
 *   backgroundColor: t.primary.alpha(0.12);
 *
 * Если предпочитаешь именованные импорты — импортируй конкретные группы:
 *   import { text, bg, primary } from "../tokens";
 */
export const t = {
    primary,
    success,
    warning,
    error,
    info,
    text,
    bg,
    action,
    divider,
    dividerAlpha,
    common,
    radius,
    spacing,
    shadows,
    duration,
    easing,
    font,
} as const;